/**
 * The check kinds: how a mission's page knows it's done. A short, fixed list, so a new mission is usually copy only.
 * Checks run in the browser and read values, addresses and lists, never page text or CSS, so a restyle can't break
 * them. This starter runs `value`, `host`, `registry`, `version` and `self`; the others arrive with the features they watch,
 * and until then their missions show as "Coming soon".
 */
import { useEffect, useState } from 'react'
import { apiGet, publicGet } from '../app/api'
import { useSession } from '../app/session'
import { screenPaths } from '../shared/screen-meta'
import type { Check, Mission } from './missions'
import { page } from './page'
import { useTicks } from './ticks'

export type CheckState =
  | { state: 'coming-soon'; note: string }
  | { state: 'waiting'; note: string }
  | { state: 'done'; note: string }
  /** Nothing to watch: the founder ticks it. */
  | { state: 'self'; note: string }

export interface CheckContext {
  hostname: string
  /** The current value in src/learn/page.ts for the check's key. */
  value: (key: string) => unknown
  /** What this browser remembered when the mission first opened. */
  first: (name: string, current: unknown) => unknown
  /** Whether a change has been seen before (for undo). */
  changedOnce: (name: string, changedNow: boolean) => boolean
  screenCount: number
  /** The preview's and the live app's versions, for `version`. Undefined until the first look. */
  ship?: ShipSnapshot
}

export const BUILT_CHECKS: Check['kind'][] = ['value', 'host', 'registry', 'self', 'version']

export function isPreviewHost(hostname: string): boolean {
  return /-preview\.cpg\.computer$/i.test(hostname)
}

/** Pure: the state of one check, given what the page can see. */
export function evaluate(mission: Mission, ctx: CheckContext): CheckState {
  if (mission.needs) return { state: 'coming-soon', note: mission.needs }
  const c = mission.check
  switch (c.kind) {
    case 'value': {
      const now = ctx.value(c.key)
      // Undo compares with the first value "Edit this page" saw, so it means "back to where you started".
      const first = ctx.first(`${c.from ?? mission.id}:${c.key}`, now)
      const changed = now !== first
      if (c.undo) {
        const seen = ctx.changedOnce(`${mission.id}:${c.key}`, changed)
        if (seen && !changed) return { state: 'done', note: `Back to "${String(first)}".` }
        return {
          state: 'waiting',
          note: seen ? `Now "${String(now)}". Waiting for it to go back to "${String(first)}".` : `Still "${String(now)}". Change it first, then undo it.`,
        }
      }
      return changed
        ? { state: 'done', note: `Was "${String(first)}", now "${String(now)}".` }
        : { state: 'waiting', note: `Still "${String(now)}".` }
    }
    case 'host':
      return isPreviewHost(ctx.hostname)
        ? { state: 'done', note: `You're on ${ctx.hostname}.` }
        : { state: 'waiting', note: `This page is on ${ctx.hostname || 'another address'}, not the preview.` }
    case 'registry': {
      const first = Number(ctx.first(`${mission.id}:screens`, ctx.screenCount))
      return ctx.screenCount > first
        ? { state: 'done', note: `${ctx.screenCount} screens now, ${first} when you started.` }
        : { state: 'waiting', note: `${ctx.screenCount} screens so far.` }
    }
    case 'version':
      return ctx.ship ? shipState(ctx.ship).check : { state: 'waiting', note: 'Looking at your preview and your live app.' }
    case 'self':
      return { state: 'self', note: 'Tick it when you have done it.' }
    default:
      return { state: 'coming-soon', note: 'This check arrives with a later update of the Learn tab.' }
  }
}

// ---------- "Ship it": the preview's version against the live app's ----------

/** What GET /api/dev/version answers on the preview (src/server/lib/dev-version.ts). */
export interface DevVersionAnswer {
  version: string
  commit: string | null
  dirty: boolean
  unpushed: number | null
  liveInHistory: boolean | null
  liveHasHead: boolean | null
}

export interface ShipSnapshot {
  /** The preview's machine, or why it couldn't be read (this page isn't on the preview, or the route didn't answer). */
  dev: DevVersionAnswer | 'unavailable'
  /** The live app's /healthz version, or why there's none. */
  live: { version: string } | 'unreachable' | 'no-address'
  /** The live address this page compared against, when it knows one. */
  liveUrl: string | null
}

/** The four steps the page draws: the change is saved, sent, being rebuilt, live. */
export const SHIP_STEPS = ['Changed', 'Sent', 'Rebuilding', 'Live'] as const

/** The live address for a preview address: fieldday-preview.cpg.computer → https://fieldday.cpg.computer. */
export function liveOriginFor(hostname: string): string | null {
  const m = /^([a-z0-9-]+)-preview\.cpg\.computer$/i.exec(hostname)
  return m ? `https://${m[1]!.toLowerCase()}.cpg.computer` : null
}

/** The commit in a version: "0.1.0+9c1e2ab" → "9c1e2ab". */
export function commitOf(version: string): string | null {
  return /\+([0-9a-f]{7,40})$/i.exec(version)?.[1]?.toLowerCase() ?? null
}

const sameCommit = (a: string, b: string) => a.startsWith(b) || b.startsWith(a)

/**
 * Pure: where a ship stands, as a check state and the step it has reached (0 to 3, 4 when live). It ticks when the
 * live app runs the commit the preview shows, or one that has it inside (someone else shipped on top).
 */
export function shipState(s: ShipSnapshot): { check: CheckState; step: number } {
  const wait = (note: string, step: number) => ({ check: { state: 'waiting' as const, note }, step })
  if (s.dev === 'unavailable' || s.live === 'no-address') {
    return wait('This check runs on your preview. Ask your agent for your preview link and open this mission there.', 0)
  }
  const dev = s.dev
  if (!dev.commit) return wait("Your preview's computer has no saved version yet.", 0)
  if (dev.dirty) return wait("Your preview has changes that aren't saved yet. Say “Ship it” and your agent saves and sends them.", 0)
  if (dev.unpushed && dev.unpushed > 0) {
    return wait(`${dev.unpushed === 1 ? 'One saved change hasn’t' : `${dev.unpushed} saved changes haven’t`} been sent yet. Your agent sends them when it ships.`, 1)
  }
  if (s.live === 'unreachable') return wait("Your live app didn't answer. It may be restarting with the new version.", 2)
  const live = commitOf(s.live.version)
  if (live && (sameCommit(live, dev.commit.toLowerCase()) || dev.liveHasHead === true)) {
    return { check: { state: 'done', note: `Your live app runs ${s.live.version}, with your preview's ${dev.commit} in it.` }, step: 4 }
  }
  if (live && dev.liveInHistory === true) return wait(`Rebuilding. Your live app still runs ${s.live.version}; your preview is at ${dev.commit}.`, 2)
  if (!live) return wait(`Your live app reports ${s.live.version}, with no version to compare. It may still be starting.`, 2)
  return wait(`Your live app runs ${live}, which this computer doesn't have. Someone else may have shipped: say “Get the latest changes and ship again.”`, 2)
}

const POLL_MS = 5_000

/** Looks at the preview's machine and the live app every few seconds while `enabled`, until a ship is live. */
export function useShipSnapshot(enabled: boolean): ShipSnapshot | undefined {
  const { getToken, refresh, end } = useSession()
  const [snap, setSnap] = useState<ShipSnapshot>()
  useEffect(() => {
    if (!enabled) return
    let live = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const liveUrl = typeof location === 'undefined' ? null : liveOriginFor(location.hostname)
    const look = async () => {
      const healthz = liveUrl
        ? await publicGet<{ version?: unknown }>(`${liveUrl}/healthz?t=${Date.now()}`).then(
            (h) => (typeof h.version === 'string' ? { version: h.version } : ('unreachable' as const)),
            () => 'unreachable' as const,
          )
        : ('no-address' as const)
      const liveCommit = typeof healthz === 'object' ? commitOf(healthz.version) : null
      const dev = await apiGet<DevVersionAnswer>(`/api/dev/version${liveCommit ? `?live=${liveCommit}` : ''}`, { getToken, refresh, end }).catch(
        () => 'unavailable' as const,
      )
      if (!live) return
      const next: ShipSnapshot = { dev, live: healthz, liveUrl }
      setSnap(next)
      if (shipState(next).step < 4) timer = setTimeout(() => void look(), POLL_MS)
    }
    void look()
    return () => {
      live = false
      if (timer) clearTimeout(timer)
    }
  }, [enabled, getToken, refresh, end])
  return enabled ? snap : undefined
}

function storage(): Pick<Storage, 'getItem' | 'setItem'> | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage
  } catch {
    return undefined
  }
}

/** The browser's side: remembers first values in this browser, and ticks the mission once it's done. */
export function browserContext(): CheckContext {
  const store = storage()
  return {
    hostname: typeof location === 'undefined' ? '' : location.hostname,
    value: (key) => (page as Record<string, unknown>)[key],
    first: (name, current) => {
      const k = `cpg.learn.first.${name}`
      const saved = store?.getItem(k)
      if (saved !== null && saved !== undefined) return JSON.parse(saved) as unknown
      try {
        store?.setItem(k, JSON.stringify(current))
      } catch {
        // storage blocked: compare against the current value
      }
      return current
    },
    changedOnce: (name, changedNow) => {
      const k = `cpg.learn.changed.${name}`
      if (changedNow) store?.setItem(k, '1')
      return store?.getItem(k) === '1'
    },
    screenCount: screenPaths.length,
  }
}

/** Each mission's state for lists (Learn home, the crash course): a tick wins, else what the page sees now. */
export function useMissionStates(): (m: Mission) => CheckState['state'] {
  const { done } = useTicks()
  const ctx = browserContext()
  return (m) => (done(m.id) ? 'done' : evaluate(m, ctx).state)
}

/** A mission's live state. Re-runs on every render, so hot reload of src/learn/page.ts is seen at once. */
export function useMissionCheck(mission: Mission): CheckState & { ticked: boolean; ship?: ShipSnapshot } {
  const { done, tick } = useTicks()
  const ticked = done(mission.id)
  const ship = useShipSnapshot(mission.check.kind === 'version' && !mission.needs && !ticked)
  const result = evaluate(mission, { ...browserContext(), ship })
  const [, force] = useState(0)
  useEffect(() => {
    if (result.state === 'done' && !ticked) void tick(mission.id, 'auto').then(() => force((n) => n + 1))
  }, [result.state, ticked, mission.id, tick])
  return { ...result, ticked, ship }
}
